import React, { useEffect, useRef } from 'react';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';

/**
 * HeaderLocalityDetector — silent, UI-less component that resolves the user's
 * precise locality (e.g. "Amanora Park Town", "Magarpatta", "Hadapsar") using
 * the Google Maps Geocoding *JavaScript SDK* and displays it under the logo.
 *
 * Why the JS SDK (not the REST endpoint):
 *   The production Google Maps key is browser-referer-restricted, so the REST
 *   endpoint returns REQUEST_DENIED. The JS SDK's Geocoder shares auth with
 *   the Maps JS API load and works fine under the same restriction. This
 *   mirrors the pattern already used by `MapLocationPicker` in Post-Ad, which
 *   reliably picks up Indian sublocality names.
 *
 * ── Cross-device consistency guarantees (iter110) ────────────────────────
 *   • ENGLISH ALWAYS — we hard-pin `language="en"` on the `APIProvider` AND
 *     pass `language: 'en'` into every `geocoder.geocode` call, so Hindi /
 *     Marathi / any other device-language variant is impossible.
 *   • REGION BIAS — `region="IN"` narrows results to India-specific
 *     administrative divisions.
 *   • FINEST-FIRST — we scan every entry in `results[]` (Google returns the
 *     coord's hierarchical breakdown from most-specific to least-specific)
 *     and pick the first sublocality/neighborhood-tagged name we find,
 *     rather than only inspecting `results[0]`.
 *   • FALLBACK CHAIN — sublocality_level_2 → sublocality_level_1 →
 *     sublocality → neighborhood → administrative_area_level_3 →
 *     administrative_area_level_2 → locality. This guarantees we surface
 *     the most granular English name Google has for that coord, and only
 *     lands on `locality` (e.g. "Pune") as an absolute last resort.
 *   • AUTO-UPDATE — we use `navigator.geolocation.watchPosition` (not
 *     `getCurrentPosition`), so as the user moves the pin refreshes. To
 *     avoid hammering the geocoder we only re-resolve when the position
 *     moves > 250 metres AND at least 20 s has passed since the last
 *     resolve.
 *
 * Renders nothing.
 */

// Great-circle distance in metres between two lat/lng pairs.
const distanceMeters = (a, b) => {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2
          + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

const MOVE_THRESHOLD_M = 250;   // only re-geocode after ~250m of movement
const MIN_RESOLVE_INTERVAL_MS = 20 * 1000;

const pickEnglishName = (results) => {
  if (!results || results.length === 0) return '';
  // Finest → coarsest priority across ALL result candidates.
  const priorities = [
    'sublocality_level_2',
    'sublocality_level_1',
    'sublocality',
    'neighborhood',
    'administrative_area_level_3',
    'administrative_area_level_2',
    'locality',
  ];
  for (const type of priorities) {
    for (const cand of results) {
      const comp = (cand.address_components || []).find((c) => c.types.includes(type));
      if (comp?.long_name) return comp.long_name;
    }
  }
  return '';
};

const GeocoderRunner = ({ onResolved }) => {
  const geocodingLib = useMapsLibrary('geocoding');
  const lastResolvedAt = useRef(0);
  const lastCoords = useRef(null);
  const watchIdRef = useRef(null);

  useEffect(() => {
    if (!geocodingLib) return;
    if (!navigator.geolocation) return;
    const geocoder = new geocodingLib.Geocoder();

    const resolve = async (lat, lng) => {
      // Debounce: skip if too close in time or space to the last resolve.
      const now = Date.now();
      if (now - lastResolvedAt.current < MIN_RESOLVE_INTERVAL_MS
          && lastCoords.current
          && distanceMeters(lastCoords.current, { lat, lng }) < MOVE_THRESHOLD_M) {
        return;
      }
      lastResolvedAt.current = now;
      lastCoords.current = { lat, lng };
      try {
        const { results } = await geocoder.geocode({
          location: { lat, lng },
          // Explicit English + India region — belt-and-suspenders with the
          // APIProvider defaults so no device locale can override us.
          language: 'en',
          region: 'IN',
        });
        const name = pickEnglishName(results);
        if (name) onResolved(name);
      } catch (_) { /* silent — outer IP fallback keeps the pin painted */ }
    };

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => resolve(pos.coords.latitude, pos.coords.longitude),
      () => { /* permission denied / unavailable — silent */ },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30 * 60 * 1000 },
    );

    return () => {
      if (watchIdRef.current != null) {
        try { navigator.geolocation.clearWatch(watchIdRef.current); } catch (_) { /* noop */ }
      }
    };
  }, [geocodingLib, onResolved]);

  return null;
};

const HeaderLocalityDetector = ({ onResolved }) => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  if (!apiKey) return null;
  return (
    <APIProvider apiKey={apiKey} libraries={['geocoding']} language="en" region="IN">
      <GeocoderRunner onResolved={onResolved} />
    </APIProvider>
  );
};

export default HeaderLocalityDetector;
